import type { TypeUploadContext } from './createUploadContext';

// ----------------------------------------------------------------------------

export type TypeRequestRegistry = {
    register: (xhr: XMLHttpRequest) => void;
    remove: (xhr: XMLHttpRequest) => void;
    markAborted: (xhr: XMLHttpRequest) => void;
    isAborted: (xhr?: XMLHttpRequest | null) => boolean;
    abortAll: () => void;
};

// ----------------------------------------------------------------------------

/*
 * Tracks the XHRs of one upload run so abort can reach them, and remembers which ones were
 * aborted. That second job needs a WeakSet alongside the context's list: an aborted request
 * still fires its error callbacks, and only this flag tells a cancellation from a failure.
 */
function createRequestRegistry(context: TypeUploadContext, onAbortAll?: () => void): TypeRequestRegistry {
    const abortedXhrs = new WeakSet<XMLHttpRequest>();

    //
    const remove = function (xhr: XMLHttpRequest): void {
        const index = context.xhrList.indexOf(xhr);

        if (index !== -1) {
            context.xhrList.splice(index, 1);
        }
    };

    //
    const abortAll = function (): void {
        if (context.abortedFlag.aborted) {
            return;
        }

        context.abortedFlag.aborted = true;

        /*
         * Releases anyone parked on the pause flag. Without this, aborting a paused run leaves
         * its workers awaiting a promise nothing will ever settle, so the run never finishes.
         * They wake, see the abort flag and bail out; `getState` reports aborted regardless.
         */
        context.pausedFlag.resume();

        for (const xhr of [...context.xhrList]) {
            abortedXhrs.add(xhr);

            try {
                xhr.abort();
            } catch {
                // ignore
            }
        }

        onAbortAll?.();
    };

    return {
        register: function (xhr): void {
            context.xhrList.push(xhr);
        },
        remove,
        markAborted: function (xhr): void {
            abortedXhrs.add(xhr);
        },
        isAborted: function (xhr): boolean {
            return xhr != null && abortedXhrs.has(xhr);
        },
        abortAll,
    };
}

// ----------------------------------------------------------------------------

export default createRequestRegistry;
